import { Injectable, inject } from '@angular/core';
import { doc, getDoc, onSnapshot, Unsubscribe } from 'firebase/firestore';

import { ACCORD_PRODUCT_ID, ProductAccessConfig } from '../models/product-access.model';
import { FirebaseService } from './firebase.service';

@Injectable({
  providedIn: 'root',
})
export class ProductAccessService {
  private readonly firebase = inject(FirebaseService);

  async getConfig(orgId: string): Promise<ProductAccessConfig> {
    const cacheKey = `crewdle_product_access_${orgId}`;

    const stored = sessionStorage.getItem(cacheKey);
    if (stored) return JSON.parse(stored) as ProductAccessConfig;

    const configRef = doc(this.firebase.firestore, 'organizations', orgId, 'product-access', 'config');
    const snapshot = await getDoc(configRef);
    // No config doc = legacy org — accord is opt-in, so no doc means no access.
    const config: ProductAccessConfig = snapshot.exists()
      ? {
          enabledProducts: snapshot.data()['enabledProducts'] || [],
          roleProductAccess: snapshot.data()['roleProductAccess'] || {},
        }
      : { enabledProducts: [], roleProductAccess: {} };

    sessionStorage.setItem(cacheKey, JSON.stringify(config));
    return config;
  }

  /**
   * Subscribe to product access config changes. Fires once on attach with the
   * current Firestore value, then again on every remote update.
   */
  subscribeToConfig(orgId: string, onChange: (config: ProductAccessConfig) => void): Unsubscribe {
    const configRef = doc(this.firebase.firestore, 'organizations', orgId, 'product-access', 'config');
    return onSnapshot(configRef, (snapshot) => {
      const config: ProductAccessConfig = snapshot.exists()
        ? {
            enabledProducts: snapshot.data()['enabledProducts'] || [],
            roleProductAccess: snapshot.data()['roleProductAccess'] || {},
          }
        : { enabledProducts: [], roleProductAccess: {} };
      sessionStorage.setItem(`crewdle_product_access_${orgId}`, JSON.stringify(config));
      onChange(config);
    });
  }

  async isAccordEnabled(orgId: string): Promise<boolean> {
    try {
      const config = await this.getConfig(orgId);
      return config.enabledProducts.includes(ACCORD_PRODUCT_ID);
    } catch {
      return false;
    }
  }
}
